/**
 * Machine Trace Inspector
 *
 * Lightweight XState inspection hook that captures machine execution traces.
 * Uses the stately.ai inspection API: https://stately.ai/docs/inspection
 *
 * The trace is a "free algebra" - a complete, deterministic record of machine
 * execution that can be:
 * 1. Replayed for debugging
 * 2. Transformed into integration tests
 * 3. Exported on error for bug reports
 * 4. Compared for regression testing
 *
 * Storage: In-memory only. Export via DevTool or programmatically on error.
 */

import type { InspectionEvent } from "xstate";
import { domEventSpy } from "../../lib/dom-event-spy";
import { dotToStateValue } from "../../machine/helper/arc-helpers";
import { getStateIdFromUrl } from "../trace-to-playwright";
import type {
  ContextDelta,
  MachineTrace,
  TraceEvent,
  UIActionHint,
} from "./types";

// ============================================================================
// Trace Inspector Class
// ============================================================================

/**
 * In-memory trace inspector that hooks into XState's inspect API
 */
export class MachineTraceInspector {
  private currentTrace: MachineTrace | null = null;
  private eventCounter = 0;
  private startTime = 0;
  private lastState = "";
  private lastChildState: string | null = null; // Child arc state, separate from root
  private lastChildSessionId: string | null = null; // Detect arc boundary crossings
  private seenChildSessions = new Set<string>(); // Reject stale snapshots from destroyed arcs
  private lastContext: Record<string, unknown> = {};
  private listeners: Set<(trace: MachineTrace) => void> = new Set();
  private maxEvents = 1000; // Prevent unbounded growth
  private pendingUIHint: UIActionHint | null = null; // Queue for UI hint that arrives before machine event
  private pendingChildEvent: TraceEvent | null = null; // Buffer child event until we know if it caused a transition

  // Child arc context tracking (for DevTool jump-to-state)
  private lastChildContext: Record<string, unknown> = {};
  private childSessionNames = new Map<string, string>(); // sessionId → actorId
  private childContextListeners: Set<() => void> = new Set();

  /**
   * Start a new trace session
   * Clears any pending DOM events from the previous session
   */
  startTrace(
    machineId: string,
    initialState: string,
    initialContext: Record<string, unknown>,
  ): void {
    this.eventCounter = 0;
    this.startTime = Date.now();
    this.lastState = initialState;
    this.lastChildState = null;
    this.lastChildSessionId = null;
    this.seenChildSessions.clear();
    this.lastContext = this.sanitizeContext(initialContext);
    this.pendingUIHint = null;
    this.pendingChildEvent = null;
    this.lastChildContext = {};
    this.childSessionNames.clear();

    // Clear DOM events from previous session (e.g., logout/restart clicks)
    domEventSpy.clear();

    const traceId = getStateIdFromUrl();

    this.currentTrace = {
      traceId: traceId || "",
      machineId,
      startedAt: new Date().toISOString(),
      initialState,
      initialContext: this.lastContext,
      events: [],
    };

    this.notifyListeners();
  }

  /**
   * XState inspect callback - pass this to createActor or useMachine
   */
  inspect = (inspectionEvent: InspectionEvent): void => {
    if (!this.currentTrace) return;

    const timestamp = Date.now();
    const relativeTime = timestamp - this.startTime;

    switch (inspectionEvent.type) {
      case "@xstate.event": {
        // Event sent to machine
        const event = inspectionEvent.event as {
          type: string;
          data?: unknown;
          output?: unknown;
        };

        // Handle actor completion events (xstate.done.actor.*)
        if (event.type.startsWith("xstate.done.actor.")) {
          // Don't clear lastChildState here — the child "done" snapshot still
          // needs it to set toState on the flushed SUBMIT event. Session boundary
          // detection (lastChildSessionId) handles staleness when the next arc starts.
          this.lastChildSessionId = null;
          // Flush (not discard) — the arc completing proves the last child event
          // drove the arc to its final state (e.g., SUBMIT on the last screen)
          if (this.pendingChildEvent) {
            this.addEvent(this.pendingChildEvent);
            this.pendingChildEvent = null;
          }
          const actorName = event.type.replace("xstate.done.actor.", "");
          this.addEvent({
            id: this.eventCounter++,
            timestamp,
            relativeTime,
            inspectionType: "@xstate.event",
            machineEvent: {
              type: event.type,
              data: event.output, // Actor output is in event.output
            },
            actorName,
            actorOutput: this.sanitizeContext(
              (event.output as Record<string, unknown>) || {},
            ),
            fromState: this.lastState,
          });
          // Clear child context — arc is done, no longer relevant for jump-to-state
          this.lastChildContext = {};
          this.notifyChildContextListeners();
          return;
        }

        // Skip other internal xstate events
        if (event.type.startsWith("xstate.")) return;

        const isChildEvent =
          inspectionEvent.actorRef.sessionId !== inspectionEvent.rootId;

        if (isChildEvent) {
          const childSessionId = inspectionEvent.actorRef.sessionId;

          // Stale event from a previously destroyed arc — ignore entirely
          if (
            this.seenChildSessions.has(childSessionId) &&
            childSessionId !== this.lastChildSessionId
          ) {
            break;
          }

          // New arc boundary: first time seeing this session
          if (!this.seenChildSessions.has(childSessionId)) {
            if (this.lastChildSessionId) {
              this.lastChildState = null;
              this.pendingChildEvent = null;
            }
            this.seenChildSessions.add(childSessionId);
          }
          this.lastChildSessionId = childSessionId;

          // Buffer child event — only record if followed by a child snapshot.
          // If no child snapshot follows (e.g., BACK with sendParent), the event
          // was forwarded to the parent and would be noise in the trace.
          const uiHint = this.pendingUIHint;
          this.pendingUIHint = null;
          this.pendingChildEvent = {
            id: this.eventCounter++,
            timestamp,
            relativeTime,
            inspectionType: "@xstate.event",
            machineEvent: {
              type: event.type,
              data: event.data,
            },
            fromState: this.lastChildState || this.lastState,
            uiHint: uiHint || undefined,
          };
          break;
        }

        // Root event: inherit UI hint from forwarded child event if we have one
        let uiHint = this.pendingUIHint;
        this.pendingUIHint = null;
        if (this.pendingChildEvent) {
          if (!uiHint && this.pendingChildEvent.uiHint) {
            uiHint = this.pendingChildEvent.uiHint;
          }
          this.pendingChildEvent = null;
        }

        this.addEvent({
          id: this.eventCounter++,
          timestamp,
          relativeTime,
          inspectionType: "@xstate.event",
          machineEvent: {
            type: event.type,
            data: event.data,
          },
          fromState: this.lastState,
          uiHint: uiHint || undefined,
        });
        break;
      }

      case "@xstate.snapshot": {
        // State updated
        const snapshot = inspectionEvent.snapshot as unknown as {
          value: string | object;
          context: Record<string, unknown>;
        };

        const newState = this.extractStateName(snapshot.value);
        const isRootActor =
          inspectionEvent.actorRef.sessionId === inspectionEvent.rootId;

        // Child actor snapshots: track state for arc-internal events but
        // don't touch root-level state tracking (lastState, lastContext).
        // Child snapshots arrive before the parent snapshot when an arc is
        // invoked, which would otherwise corrupt lastState and lastEvent.toState.
        if (!isRootActor) {
          const childSessionId = inspectionEvent.actorRef.sessionId;

          // Skip non-machine actors (promise/callback actors invoked inside
          // an arc). They don't have state values, and processing them would
          // clear lastChildState via boundary detection without being able to
          // restore it — corrupting arc state tracking.
          if (!newState) return;

          // Stale snapshot from a previously destroyed arc — ignore entirely
          if (
            this.seenChildSessions.has(childSessionId) &&
            childSessionId !== this.lastChildSessionId
          ) {
            return;
          }

          // New arc boundary: first time seeing this session
          if (!this.seenChildSessions.has(childSessionId)) {
            if (this.lastChildSessionId) {
              this.lastChildState = null;
              this.pendingChildEvent = null;
            }
            this.seenChildSessions.add(childSessionId);
          }
          this.lastChildSessionId = childSessionId;

          // Populate childSessionNames as a fallback in case the @xstate.actor
          // event fired before startTrace was called (React effect ordering means
          // @xstate.actor can fire in useMachine's effect, then startTrace clears
          // childSessionNames in a later effect). actorRef.id is available here too.
          // biome-ignore lint/suspicious/noExplicitAny: actorRef.id is not in the narrow InspectionEvent type
          const actorId = (inspectionEvent.actorRef as any).id as
            | string
            | undefined;
          if (actorId && !this.childSessionNames.has(childSessionId)) {
            this.childSessionNames.set(childSessionId, actorId);
          }

          const prevChildState = this.lastChildState;
          this.lastChildState = newState;

          // Only flush buffered child event if the child state actually changed.
          // XState fires a snapshot even for same-state events (e.g., BACK with
          // sendParent and no target) — those are forwarded, not real transitions.
          const childStateChanged = newState !== prevChildState;
          if (this.pendingChildEvent && childStateChanged) {
            this.addEvent(this.pendingChildEvent);
            this.pendingChildEvent = null;
          }

          // Set toState on the most recent unresolved child event. Search backward
          // because actor completion events may be interleaved (e.g., actor completion
          // fires before the child "done" snapshot, inserting an event between the
          // flushed SUBMIT and this snapshot).
          if (childStateChanged && prevChildState) {
            for (let i = this.currentTrace.events.length - 1; i >= 0; i--) {
              const evt = this.currentTrace.events[i];
              if (evt.toState) break; // Past the unresolved window
              if (
                evt.inspectionType === "@xstate.event" &&
                evt.fromState === prevChildState
              ) {
                evt.toState = newState;
                break;
              }
            }
          }

          // Record child state transitions so analytics can combine them
          if (prevChildState && newState !== prevChildState) {
            this.addEvent({
              id: this.eventCounter++,
              timestamp,
              relativeTime,
              inspectionType: "@xstate.snapshot",
              fromState: prevChildState,
              toState: newState,
            });
          }

          // Capture child context for DevTool jump-to-state support.
          // Always update so the latest form data is available for copy.
          const sanitizedChildContext = this.sanitizeContext(snapshot.context);
          const childContextChanged = !this.deepEqual(
            this.lastChildContext,
            sanitizedChildContext,
          );
          if (childContextChanged) {
            this.lastChildContext = sanitizedChildContext;
          }
          // Notify on state OR context change so the DevTool copy always has
          // the current stateName even when the arc context hasn't changed
          // (e.g., sitting on a verify screen after a machine-driven transition).
          if (childStateChanged || childContextChanged) {
            this.notifyChildContextListeners();
          }

          return;
        }

        // Only discard buffered child event if the ROOT state actually changed.
        // When a child transitions internally, XState may fire the root snapshot
        // (same state, updated children map) BEFORE the child snapshot. If we
        // unconditionally discard here, the child event is lost. Let the child
        // snapshot handler flush it instead. If root state changed, the event was
        // forwarded (sendParent) — already handled by the root event handler above.
        // Stale events from destroyed arcs are cleaned up by arc boundary detection.
        const rootStateChanged = newState && newState !== this.lastState;
        if (rootStateChanged) {
          this.pendingChildEvent = null;
        }

        const sanitizedContext = this.sanitizeContext(snapshot.context);

        // Skip snapshots with empty/invalid state (e.g., when snapshot.value is undefined)
        if (!newState) {
          // Still update context for delta tracking
          this.lastContext = sanitizedContext;
          return;
        }

        // Compute context delta instead of storing full snapshot
        const delta = this.computeContextDelta(
          this.lastContext,
          sanitizedContext,
        );

        // Set toState on the most recent unresolved event. Search backward
        // because child snapshot events may have been interleaved (they
        // already have toState set, so they're skipped).
        if (newState) {
          for (let i = this.currentTrace.events.length - 1; i >= 0; i--) {
            const evt = this.currentTrace.events[i];
            if (evt.toState) continue;
            evt.toState = newState;
            break;
          }
        }

        // Only store delta if there are changes
        const hasDelta =
          Object.keys(delta.added).length > 0 ||
          Object.keys(delta.changed).length > 0 ||
          delta.removed.length > 0;

        // Only record snapshot if state actually changed (skip intermediate action snapshots)
        const stateChanged = newState && newState !== this.lastState;

        if (stateChanged) {
          this.addEvent({
            id: this.eventCounter++,
            timestamp,
            relativeTime,
            inspectionType: "@xstate.snapshot",
            fromState: this.lastState,
            toState: newState,
            contextDelta: hasDelta ? delta : undefined,
          });
          this.lastState = newState;
        } else if (hasDelta) {
          // State didn't change but context did - update last event's delta or add context-only snapshot
          const lastSnapshotEvent = [...this.currentTrace.events]
            .reverse()
            .find((e) => e.inspectionType === "@xstate.snapshot");
          if (lastSnapshotEvent && lastSnapshotEvent.toState === newState) {
            // Merge context delta into last snapshot
            if (!lastSnapshotEvent.contextDelta) {
              lastSnapshotEvent.contextDelta = delta;
            } else {
              // Merge deltas
              Object.assign(lastSnapshotEvent.contextDelta.added, delta.added);
              Object.assign(
                lastSnapshotEvent.contextDelta.changed,
                delta.changed,
              );
              lastSnapshotEvent.contextDelta.removed.push(...delta.removed);
            }
            this.notifyListeners();
          }
        }

        // Update lastContext for next delta computation
        this.lastContext = sanitizedContext;
        break;
      }

      case "@xstate.actor": {
        // Actor started — only place where actorRef.id is available alongside sessionId.
        // Build a sessionId → actorId map so we can name child arc context snapshots.
        const actorRef = inspectionEvent.actorRef as {
          id?: string;
          sessionId: string;
        };
        if (actorRef.id && actorRef.sessionId !== inspectionEvent.rootId) {
          this.childSessionNames.set(actorRef.sessionId, actorRef.id);
        }
        break;
      }

      case "@xstate.microstep": {
        // Granular transition step (useful for eventless transitions)
        // We could capture this but for now keep it minimal
        break;
      }
    }
  };

  /**
   * Queue UI action hint to be attached to the next machine event
   * Called from kiosk-renderer when onEvent fires (before send() is called)
   *
   * The timing is:
   * 1. User clicks button -> DOM event captured
   * 2. onEvent fires -> addUIHint called (queues the hint)
   * 3. send() called -> inspect callback fires -> hint attached to event
   */
  addUIHint(hint: UIActionHint): void {
    if (!this.currentTrace) return;

    // Queue the hint - it will be attached when the next @xstate.event is recorded
    this.pendingUIHint = hint;
  }

  /**
   * End the current trace session
   */
  endTrace(): MachineTrace | null {
    if (!this.currentTrace) return null;

    this.currentTrace.endedAt = new Date().toISOString();
    const trace = this.currentTrace;

    this.notifyListeners();
    return trace;
  }

  /**
   * Export trace with error info (call when error detected)
   */
  exportWithError(
    error: Error,
    currentState: string,
    currentContext: Record<string, unknown>,
  ): MachineTrace | null {
    if (!this.currentTrace) return null;

    this.currentTrace.endedAt = new Date().toISOString();
    this.currentTrace.error = {
      message: error.message,
      stack: error.stack,
      state: currentState,
      context: this.sanitizeContext(currentContext),
    };

    return { ...this.currentTrace };
  }

  /**
   * Get current trace (read-only snapshot)
   */
  getTrace(): MachineTrace | null {
    return this.currentTrace ? { ...this.currentTrace } : null;
  }

  /**
   * Get events for display (most recent first option)
   */
  getEvents(limit?: number, newestFirst = false): TraceEvent[] {
    if (!this.currentTrace) return [];

    let events = [...this.currentTrace.events];
    if (newestFirst) events.reverse();
    if (limit) events = events.slice(0, limit);

    return events;
  }

  /**
   * Clear current trace
   */
  clearTrace(): void {
    this.currentTrace = null;
    this.eventCounter = 0;
    this.notifyListeners();
  }

  /**
   * Subscribe to trace updates
   */
  subscribe(listener: (trace: MachineTrace) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Subscribe to child arc context changes (for DevTool jump-to-state).
   * Fires whenever the arc's context updates. Call getChildSnapshot() inside
   * the listener to read the latest value.
   */
  subscribeToChildContext(listener: () => void): () => void {
    this.childContextListeners.add(listener);
    return () => this.childContextListeners.delete(listener);
  }

  /**
   * Get the current child arc snapshot for DevTool copy/paste.
   * Returns the XState state value (string for flat states, nested object
   * for compound states like `{ ocrLicense: "licenseFrontPhoto" }`).
   * Returns null when no arc is active.
   */
  getChildSnapshot(): {
    arcId: string;
    stateName: string | Record<string, unknown>;
    context: Record<string, unknown>;
  } | null {
    if (!this.lastChildSessionId || !this.lastChildState) return null;
    // Look up actor ID lazily — @xstate.actor fires after the first snapshot,
    // so reading here (from React) guarantees the map is already populated.
    const arcId = this.childSessionNames.get(this.lastChildSessionId);
    if (!arcId) return null;
    return {
      arcId,
      stateName: dotToStateValue(this.lastChildState),
      context: this.lastChildContext,
    };
  }

  /**
   * Export trace as JSON string
   */
  exportAsJSON(): string | null {
    if (!this.currentTrace) return null;
    return JSON.stringify(this.currentTrace, null, 2);
  }

  /**
   * Export trace as downloadable file
   */
  downloadTrace(filename?: string): void {
    const json = this.exportAsJSON();
    if (!json) return;

    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || `kiosk-trace-${this.currentTrace?.traceId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ============================================================================
  // Private helpers
  // ============================================================================

  private addEvent(event: TraceEvent): void {
    if (!this.currentTrace) return;

    this.currentTrace.events.push(event);

    // Prevent unbounded growth
    if (this.currentTrace.events.length > this.maxEvents) {
      this.currentTrace.events.shift();
    }

    this.notifyListeners();
  }

  private notifyListeners(): void {
    if (!this.currentTrace) return;
    const trace = { ...this.currentTrace };
    for (const listener of this.listeners) {
      listener(trace);
    }
  }

  private notifyChildContextListeners(): void {
    for (const listener of this.childContextListeners) {
      listener();
    }
  }

  private generateTraceId(): string {
    return `trace-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  /**
   * Compute delta between two context objects
   * Returns only what changed, was added, or was removed
   */
  private computeContextDelta(
    oldContext: Record<string, unknown>,
    newContext: Record<string, unknown>,
  ): ContextDelta {
    const added: Record<string, unknown> = {};
    const changed: Record<string, { from: unknown; to: unknown }> = {};
    const removed: string[] = [];

    // Find added and changed keys
    for (const key of Object.keys(newContext)) {
      if (!(key in oldContext)) {
        added[key] = newContext[key];
      } else if (!this.deepEqual(oldContext[key], newContext[key])) {
        changed[key] = { from: oldContext[key], to: newContext[key] };
      }
    }

    // Find removed keys
    for (const key of Object.keys(oldContext)) {
      if (!(key in newContext)) {
        removed.push(key);
      }
    }

    return { added, changed, removed };
  }

  /**
   * Deep equality check for context values
   */
  private deepEqual(a: unknown, b: unknown): boolean {
    if (a === b) return true;
    if (typeof a !== typeof b) return false;
    if (a === null || b === null) return a === b;
    if (typeof a !== "object") return false;

    // Arrays
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a.every((val, i) => this.deepEqual(val, b[i]));
    }

    // Objects
    if (Array.isArray(a) !== Array.isArray(b)) return false;
    const aObj = a as Record<string, unknown>;
    const bObj = b as Record<string, unknown>;
    const aKeys = Object.keys(aObj);
    const bKeys = Object.keys(bObj);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every((key) => this.deepEqual(aObj[key], bObj[key]));
  }

  private extractStateName(value: string | object | undefined): string {
    // Handle undefined/null - return empty string to signal invalid state
    if (value === undefined || value === null) {
      return "";
    }
    if (typeof value === "string") return value;
    if (typeof value === "object") {
      // Recursively extract nested state names
      // e.g., { ocrLicense: "licenseFrontPhoto" } → "ocrLicense.licenseFrontPhoto"
      // e.g., { ocrLicense: { nested: "deep" } } → "ocrLicense.nested.deep"
      const [parentName, childValue] = Object.entries(value)[0] ?? [];
      if (!parentName) return "";
      const childName = this.extractStateName(childValue as string | object);
      return childName ? `${parentName}.${childName}` : parentName;
    }
    return "";
  }

  /**
   * Sanitize context for storage - remove sensitive data, circular refs
   */
  private sanitizeContext(
    context: Record<string, unknown>,
  ): Record<string, unknown> {
    const sensitiveKeys = ["otp", "password", "token", "secret", "apiKey"];

    try {
      const sanitized = { ...context };

      for (const key of sensitiveKeys) {
        if (key in sanitized) {
          sanitized[key] = "[REDACTED]";
        }
      }

      // Remove photo base64 data (too large)
      for (const key of Object.keys(sanitized)) {
        if (key.includes("Photo") && typeof sanitized[key] === "string") {
          const val = sanitized[key] as string;
          if (val.length > 1000) {
            sanitized[key] = `[BASE64_IMAGE: ${val.length} chars]`;
          }
        }
      }

      // Test serialization to catch circular refs
      JSON.stringify(sanitized);
      return sanitized;
    } catch {
      return { _error: "Context could not be serialized" };
    }
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

/**
 * Global trace inspector instance
 * Use this in kiosk-provider.tsx and kiosk-renderer.tsx
 */
export const traceInspector = new MachineTraceInspector();
